import queryString from 'query-string';
import { useMemo } from 'react';
import { useLocation } from 'react-router';

import { SearchFilterContentTypes } from '../../Shared/enums';
import { ImageSelectedSearchFilterOptions } from '../containers/MenuContainerInterfaces';
import { categories } from '../entities/ImageSearchFilterOptions';
import ImageSearchOptions from '../entities/ImageSearchOptions';
import { UsageRights } from '../entities/UsageRights';
import { getDecodedSearchTerm } from '../utils/searchUtils';
import buildContentAgnosticSearchOptions from './buildContentAgnosticSearchOptions';

interface ImageSearchPageQueryParams {
  // A urlId of a single category.
  categories?: string;
  color?: string;
  hasPropertyReleased?: boolean;
  hasTalentReleased?: boolean;
  hasTransparency?: boolean;
  'media-type'?: string;
  orientation?: string;
  usage_rights?: UsageRights;
}

export default function useImageSearchOptionsFromLocation(): Partial<ImageSelectedSearchFilterOptions> {
  const { pathname, search, key } = useLocation();

  const selectedSearchFilterOptions = useMemo<
    Partial<ImageSelectedSearchFilterOptions>
  >(() => {
    const queryParams: ImageSearchPageQueryParams = queryString.parse(search, {
      parseBooleans: true,
      parseNumbers: false,
    });

    const updates: Partial<ImageSelectedSearchFilterOptions> =
      buildContentAgnosticSearchOptions(queryParams);

    // contentType
    switch (queryParams['media-type']) {
      case 'photos':
        updates.contentType = SearchFilterContentTypes.Photos;
        break;
      case 'snapshots':
        updates.contentType = SearchFilterContentTypes.Snapshots;
        break;
      case 'vectors':
        updates.contentType = SearchFilterContentTypes.Vectors;
        break;
      case 'illustrations':
        updates.contentType = SearchFilterContentTypes.Illustrations;
        break;
    }

    // query param names from ImageSearchQueryParams.php and SearchQueryParams.php
    // also see ImageRouteTranslator::getAllowedParams()

    // categories
    if (queryParams.categories && updates.contentType) {
      updates.categories = categories.find(
        ({ contentType, urlId }) =>
          contentType === updates.contentType && urlId == queryParams.categories
      )?.value;
    }

    // property release
    if (queryParams.hasPropertyReleased) {
      updates.propertyReleased = queryParams.hasPropertyReleased;
    }

    // talent release
    if (queryParams.hasTalentReleased) {
      updates.talentReleased = queryParams.hasTalentReleased;
    }

    // transparency
    if (queryParams.hasTransparency) {
      updates.hasTransparency = queryParams.hasTransparency;
    }

    // orientation
    if (queryParams.orientation) {
      updates.orientation = queryParams.orientation;
    }

    // usage rights
    if (queryParams.usage_rights) {
      updates.usageRights = queryParams.usage_rights;
    }

    // color
    if (queryParams.color) {
      updates.color = queryParams.color;
    }

    // search term
    const searchTerm = /\/images\/search\/(.*)/.exec(pathname)?.[1];
    if (searchTerm) {
      updates.searchTerm = getDecodedSearchTerm(searchTerm);
    }

    return new ImageSearchOptions().update(updates);
  }, [pathname, search, key]);

  return selectedSearchFilterOptions;
}
